import React, { useState, useEffect } from 'react';
import './LongRunningLoadingState.css';

export interface LongRunningLoadingStateProps {
  /** The operation being performed */
  operation: 'idea-generation' | 'validation-survey' | 'launch-assets' | 'pre-release';
  /** Whether the operation is currently running */
  isLoading: boolean;
  /** Optional custom message */
  customMessage?: string;
  /** Optional estimated time in seconds */
  estimatedTime?: number;
}

const OPERATION_CONFIG = {
  'idea-generation': {
    title: 'Generating Business Ideas',
    title_zh: '正在生成商业创意',
    messages: [
      'Connecting to AI service...',
      'Analyzing your skills and interests...',
      'Researching market opportunities...',
      'Evaluating technical feasibility...',
      'Crafting personalized business ideas...',
      'Finalizing your ideas...',
      'Almost there...'
    ],
    messages_zh: [
      '正在连接 AI 服务...',
      '正在分析您的技能和兴趣...',
      '正在研究市场机会...',
      '正在评估技术可行性...',
      '正在制定个性化商业创意...',
      '正在完成您的创意...',
      '即将完成...'
    ],
    estimatedTime: 120,
    icon: '🤖'
  },
  'validation-survey': {
    title: 'Creating Validation Survey',
    title_zh: '正在创建验证调查',
    messages: [
      'Analyzing your business idea...',
      'Generating validation questions...',
      'Setting up survey structure...',
      'Finalizing survey...'
    ],
    messages_zh: [
      '正在分析您的商业创意...',
      '正在生成验证问题...',
      '正在设置调查结构...',
      '正在完成调查...'
    ],
    estimatedTime: 20,
    icon: '📊'
  },
  'launch-assets': {
    title: 'Generating Launch Assets',
    title_zh: '正在生成发布资产',
    messages: [
      'Creating marketing materials...',
      'Generating social media content...',
      'Designing promotional assets...',
      'Preparing launch package...',
      'Finalizing assets...'
    ],
    messages_zh: [
      '正在创建营销材料...',
      '正在生成社交媒体内容...',
      '正在设计促销资产...',
      '正在准备发布包...',
      '正在完成资产...'
    ],
    estimatedTime: 40,
    icon: '🚀'
  },
  'pre-release': {
    title: 'Creating Pre-Release Project',
    title_zh: '正在创建预发布项目',
    messages: [
      'Analyzing your business idea...',
      'Generating landing page content...',
      'Creating beta program materials...',
      'Setting up project structure...',
      'Finalizing pre-release project...'
    ],
    messages_zh: [
      '正在分析您的商业创意...',
      '正在生成着陆页内容...',
      '正在创建测试版计划材料...',
      '正在设置项目结构...',
      '正在完成预发布项目...'
    ],
    estimatedTime: 35,
    icon: '🎯'
  }
};

export const LongRunningLoadingState: React.FC<LongRunningLoadingStateProps> = ({
  operation,
  isLoading,
  customMessage,
  estimatedTime
}) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [language, setLanguage] = useState<'en' | 'zh'>('en');

  const config = OPERATION_CONFIG[operation];
  const messages = language === 'zh' ? config.messages_zh : config.messages;
  const title = language === 'zh' ? config.title_zh : config.title;
  const totalTime = estimatedTime || config.estimatedTime;

  // Detect language from document
  useEffect(() => {
    const htmlLang = document.documentElement.lang;
    if (htmlLang === 'zh' || htmlLang === 'zh-CN') {
      setLanguage('zh');
    }
  }, []);

  // Cycle through messages
  useEffect(() => {
    if (!isLoading) {
      setCurrentMessageIndex(0);
      setElapsedTime(0);
      return;
    }

    const messageInterval = Math.floor((totalTime * 1000) / messages.length);
    const timer = setInterval(() => {
      setCurrentMessageIndex((prev) => {
        if (prev < messages.length - 1) {
          return prev + 1;
        }
        return prev;
      });
    }, messageInterval);

    return () => clearInterval(timer);
  }, [isLoading, messages.length, totalTime]);

  // Track elapsed time
  useEffect(() => {
    if (!isLoading) {
      setElapsedTime(0);
      return;
    }

    const timer = setInterval(() => {
      setElapsedTime((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [isLoading]);

  if (!isLoading) {
    return null;
  }

  const progress = Math.min((elapsedTime / totalTime) * 100, 95); // Cap at 95% until complete

  return (
    <div className="long-running-loading-overlay">
      <div className="long-running-loading-container">
        <div className="long-running-loading-icon">
          <span className="loading-icon-emoji">{config.icon}</span>
          <div className="loading-spinner-ring"></div>
        </div>
        
        <h3 className="long-running-loading-title">{title}</h3>
        
        <div className="long-running-loading-message">
          {customMessage || messages[currentMessageIndex]}
        </div>

        <div className="long-running-loading-progress">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <div className="progress-text">
            {language === 'zh' 
              ? `预计还需 ${Math.max(0, totalTime - elapsedTime)} 秒...` 
              : `Estimated ${Math.max(0, totalTime - elapsedTime)}s remaining...`}
          </div>
        </div>

        <div className="long-running-loading-tips">
          <p className="loading-tip">
            {operation === 'idea-generation' 
              ? (language === 'zh'
                  ? '💡 提示：AI 正在深度分析以生成高质量的创意。这通常需要 3-5 分钟。请保持此窗口打开。'
                  : '💡 Tip: Our AI is performing deep analysis to generate high-quality ideas. This typically takes 3-5 minutes. Please keep this window open.')
              : (language === 'zh' 
                  ? '💡 提示：这个过程可能需要一些时间。请保持此窗口打开。' 
                  : '💡 Tip: This process may take a while. Please keep this window open.')
            }
          </p>
        </div>
      </div>
    </div>
  );
};
